<template>
  <div>
    <button
      type="button"
      class="btn btn-primary btn-with-addon"
      @click="showModal"
    >
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-plus-circle" />
      </span>
      Lesson Plan
    </button>

    <a-modal v-model="visible" title="Add a new lesson plan" width="800px">
      <template slot="footer">
        <a-button key="back" @click="handleCancel">Cancel</a-button>
        <a-button key="submit" type="primary" :loading="loading" @click="handleSubmit">Add Lesson Plan</a-button>
      </template>

      <a-form class="mb-4 row" :form="form">
        <a-form-item label="Session" class="col-md-6">
          <a-select
            v-decorator="[
              'session_id',
              {rules: [{ required: true, message: 'Please select a session' }]},
            ]"
            placeholder="Select a session"
            @change="handleSessionChange"
          >
            <a-select-option v-for="s in schoolSessions" :key="s.id" :value="s.id">
              {{ s.name }}
              <small class="text-primary ml-2">{{ currentSession.id === s.id ? 'Current' : '' }}</small>
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Class" class="col-md-6">
          <a-select
            v-decorator="[
              'class_id',
              {rules: [{ required: true, message: 'Please select a class' }]},
            ]"
            placeholder="Select a class"
            @change="handleClassChange"
          >
            <a-select-option v-for="c in classes" :key="c.id" :value="c.id">
              {{ c.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Subject" class="col-md-6">
          <a-select
            v-decorator="[
              'subject_id',
              {rules: [{ required: true, message: 'Please select a subject' }]},
            ]"
            placeholder="Select a subject"
          >
            <a-select-option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Name" class="col-md-6">
          <a-input
            v-decorator="[
              'name',
              {rules: [{ required: true, message: 'Please input a name' }]},
            ]"
            placeholder="Enter lesson plan name"
          />
        </a-form-item>

        <a-form-item label="Start Date" class="col-md-6">
          <a-date-picker
              v-decorator="[
              'start_date',
              {rules: [{ required: true, message: 'Please input a start date' }]},
              ]"
              :format=dateFormat />
        </a-form-item>
        <a-form-item label="End Date" class="col-md-6">
          <a-date-picker
              v-decorator="[
              'end_date',
              {rules: [{ required: true, message: 'Please input an end date' }]},
              ]"
              :format=dateFormat />
        </a-form-item>

        <div class="col-md-12">
          <hr/>
          <h6>Add Lesson Units and Topics</h6>
          <div>Add units and their corresponding chapters/topics for this lesson plan.</div>
          <div v-for="(topicKey, topicIndex) in topicKeys" :key="topicKey">
            <div class="row">
              <a-form-item :label="`Unit ${topicIndex + 1}`" class="col-md-4">
                <a-input
                  v-decorator="[
                    `topics[${topicKey}].topic_name`,
                    {rules: [{ required: true, message: 'Please input a Unit name' }]},
                  ]"
                  placeholder="Enter unit name"
                />
              </a-form-item>
              <a-form-item label="Academic Term" class="col-md-3">
                <a-select
                  v-decorator="[
                    `topics[${topicKey}].academic_term_id`,
                    {rules: [{ required: true, message: 'Please select an academic term' }]},
                  ]"
                  placeholder="Select an academic term"
                >
                  <a-select-option v-for="term in academicTerms" :key="term.id" :value="term.id">
                    {{ term.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="Total Mark" class="col-md-2">
                <a-input-number
                  v-decorator="[
                  `topics[${topicKey}].marks`,
                    { initialValue: 0, rules: [{ required: true, message: 'Enter total mark' }]},
                    ]"
                  :min="0"
                  placeholder="Mark"
                  style="width: 100%;"
                />
              </a-form-item>

              <!-- Total Period/Day -->
              <a-form-item label="Total Period/Day" class="col-md-2">
                <a-input-number
                  v-decorator="[
                    `topics[${topicKey}].total_periods`,
                    { initialValue: 1, rules: [{ required: true, message: 'Enter total period/day' }]},
                    ]"
                  :min="1"
                  placeholder="Period"
                  style="width: 100%;"
                />
              </a-form-item>

              <div class="col-md-1">
                <a-icon
                  class="dynamic-delete-button"
                  type="minus-circle-o"
                  @click="() => removeTopic(topicKey)"
                />
              </div>
            </div>

            <div v-for="(subTopicKey, subTopicIndex) in subTopicKeys[topicKey]" :key="subTopicKey">
              <div class="row">
                <a-form-item :label="`Unit/Chapter ${subTopicIndex + 1}`" style="margin-left: 40px;" class="col-md-6">
                  <a-input
                    v-decorator="[
                      `topics[${topicKey}].sub_topics[${subTopicKey}].sub_topic_name`,
                      {rules: [{ required: true, message: 'Please input a unit/chapter name' }]},
                    ]"
                    placeholder="Enter unit/chapter name"
                  />
                  </a-form-item>
                  <a-form-item label="Estimated Period" class="col-md-3">
                  <a-input-number
                    v-decorator="[
                      `topics[${topicKey}].sub_topics[${subTopicKey}].estimated_periods`,
                      {initialValue: 1, rules: [{ required: true, message: 'Please input estimated period' }]},
                    ]"
                    :min="1"
                    placeholder="Period"
                  />
                  </a-form-item>
                  <div class="col-md-2">
                  <a-icon
                    class="dynamic-delete-button"
                    type="minus-circle-o"
                    @click="() => removeSubTopic(topicKey, subTopicKey)"
                  />
                  </div>
                </div>
            </div>
            <a-button type="dashed" style="margin-left: 40px;" @click="() => addSubTopic(topicKey)">
              <a-icon type="plus" /> Add Unit/Chapter
            </a-button>
          </div>
          <a-button type="dashed" @click="addTopic">
            <a-icon type="plus" /> Add Unit
          </a-button>
        </div>

      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { find } from 'lodash'

export default {
  name: 'AddLessonPlan',
  data: function() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      visible: false,
      classes: [],
      subjects: [],
      schoolSessions: [],
      selectedSession: undefined,
      academicTerms: [],
      topicKeys: [],
      nextTopicKey: 0,
      subTopicKeys: {},
      nextSubTopicKey: 0,
      dateFormat: 'YYYY-MM-DD',
    }
  },
  computed: {
    currentSession: function() {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
  },
  methods: {
    fetchSessions() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        this.selectedSession = this.currentSession.id
        this.form.setFieldsValue({ session_id: this.selectedSession })
        this.fetchClasses(this.selectedSession)
        this.fetchAcademicTerms(this.selectedSession)
      })
    },
    fetchClasses(sessionId) {
      this.$http.get(`/api/class?session_id=${sessionId}`).then(({ data }) => {
        this.classes = data.data
      })
    },
    fetchAcademicTerms(sessionId) {
      this.$http.get(`/api/lesson-plan/academic-terms?session_id=${sessionId}`).then(({ data }) => {
        this.academicTerms = data.data
      })
    },
    handleSessionChange(sessionId) {
      this.selectedSession = sessionId
      this.fetchClasses(sessionId)
      this.fetchAcademicTerms(sessionId)
    },
    handleClassChange(classId) {
      this.fetchSubjects(classId)
    },
    fetchSubjects(classId) {
      this.$http.get(`/api/subject?class_id=${classId}`).then(({ data }) => {
        this.subjects = data.data
      })
    },
    addTopic() {
      const newKey = this.nextTopicKey++
      this.topicKeys = this.topicKeys.concat(newKey)
      this.$set(this.subTopicKeys, newKey, [])
    },
    removeTopic(k) {
      this.topicKeys = this.topicKeys.filter(key => key !== k)
      this.$delete(this.subTopicKeys, k)
    },
    addSubTopic(topicKey) {
      const newSubKey = this.nextSubTopicKey++
      this.subTopicKeys[topicKey] = this.subTopicKeys[topicKey].concat(newSubKey)
    },
    removeSubTopic(topicKey, subTopicKey) {
      this.subTopicKeys[topicKey] = this.subTopicKeys[topicKey].filter(key => key !== subTopicKey)
    },
    showModal() {
      this.visible = true
      this.fetchSessions()
      this.topicKeys = []
      this.nextTopicKey = 0
      this.subTopicKeys = {}
      this.nextSubTopicKey = 0
    },
    handleSubmit(e) {
      this.loading = true
      this.form.validateFields((err, values) => {
        if (!err) {
          if (values.topics) {
            values.topics = values.topics.filter(topic => topic)
            values.topics.forEach(topic => {
              if (topic.sub_topics) {
                topic.sub_topics = topic.sub_topics.filter(subTopic => subTopic)
              }
            })
          }

          if (values.start_date) {
            values.start_date = values.start_date.format('YYYY-MM-DD')
          }
          if (values.end_date) {
            values.end_date = values.end_date.format('YYYY-MM-DD')
          }

          this.$confirm({
            title: `Are you sure?`,
            content: `Click on the OK button to add the new lesson plan!`,
            onOk: () => {
              this.$http
                .post('/api/lesson-plan/plans', values)
                .then(({ data }) => {
                  this.$emit('created')
                  this.loading = false
                  this.visible = false
                  this.$notification['success']({
                    message: 'Lesson plan successfully added',
                    description: `${data.data.name} added successfully!`,
                  })
                  this.form.resetFields()
                })
                .catch(error => {
                  this.loading = false
                  this.$neoNotify.error(error)
                })
            },
            onCancel: () => {
              this.loading = false
            },
          })
        }

        this.loading = false
      })
    },
    handleCancel(e) {
      this.visible = false
    },
  },
}
</script>
<style scoped>
.dynamic-delete-button {
  cursor: pointer;
  position: relative;
  top: 4px;
  font-size: 24px;
  color: #999;
  transition: all 0.3s;
}
.dynamic-delete-button:hover {
  color: #777;
}
</style>
